/**
 * Entitlements API + TanStack Query hooks.
 *
 * Backend endpoints:
 *   GET    /v1/catalog/entitlements?student_id=&status=
 *   GET    /v1/catalog/entitlements/{id}
 *   GET    /v1/catalog/entitlements/by-student/{student_id}
 *   POST   /v1/catalog/entitlements
 *   POST   /v1/catalog/entitlements/{id}/freeze
 *   POST   /v1/catalog/entitlements/{id}/unfreeze
 *   POST   /v1/catalog/entitlements/{id}/cancel
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@core/api/client';
import type { components } from '@api/index';

export type EntitlementResponse = components['schemas']['EntitlementResponse'];
export type EntitlementCreate = components['schemas']['EntitlementCreate'];
export type EntitlementFreezeRequest = components['schemas']['EntitlementFreezeRequest'];
export type EntitlementCancelRequest = components['schemas']['EntitlementCancelRequest'];
export type PaginatedEntitlements =
  components['schemas']['PaginatedResult_EntitlementResponse_'];

export interface ListEntitlementsParams {
  student_id?: string;
  status?: string;
}

export const entitlementKeys = {
  all: ['catalog', 'entitlements'] as const,
  list: (params: ListEntitlementsParams) =>
    ['catalog', 'entitlements', 'list', params] as const,
  byStudent: (studentId: string) =>
    ['catalog', 'entitlements', 'by-student', studentId] as const,
  detail: (id: string) => ['catalog', 'entitlements', 'detail', id] as const,
};

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export async function listEntitlements(
  params: ListEntitlementsParams = {},
): Promise<PaginatedEntitlements> {
  return api.get<PaginatedEntitlements>('/v1/catalog/entitlements', {
    query: {
      student_id: params.student_id || undefined,
      status: params.status || undefined,
    },
  });
}

export async function listStudentEntitlements(
  studentId: string,
): Promise<EntitlementResponse[]> {
  return api.get<EntitlementResponse[]>(
    `/v1/catalog/entitlements/by-student/${studentId}`,
  );
}

export async function createEntitlement(
  body: EntitlementCreate,
): Promise<EntitlementResponse> {
  return api.post<EntitlementResponse>('/v1/catalog/entitlements', body);
}

export async function freezeEntitlement(
  id: string,
  body: EntitlementFreezeRequest,
): Promise<EntitlementResponse> {
  return api.post<EntitlementResponse>(`/v1/catalog/entitlements/${id}/freeze`, body);
}

export async function unfreezeEntitlement(id: string): Promise<EntitlementResponse> {
  return api.post<EntitlementResponse>(`/v1/catalog/entitlements/${id}/unfreeze`);
}

export async function cancelEntitlement(
  id: string,
  body: EntitlementCancelRequest,
): Promise<EntitlementResponse> {
  return api.post<EntitlementResponse>(`/v1/catalog/entitlements/${id}/cancel`, body);
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useEntitlements(params: ListEntitlementsParams = {}) {
  return useQuery({
    queryKey: entitlementKeys.list(params),
    queryFn: () => listEntitlements(params),
    staleTime: 30_000,
  });
}

export function useStudentEntitlements(studentId: string | undefined) {
  return useQuery({
    queryKey: entitlementKeys.byStudent(studentId ?? ''),
    queryFn: () => listStudentEntitlements(studentId!),
    enabled: Boolean(studentId),
    staleTime: 30_000,
  });
}

function invalidateAllEntitlements(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: entitlementKeys.all });
}

export function useCreateEntitlement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createEntitlement,
    onSuccess: () => invalidateAllEntitlements(qc),
  });
}

export function useFreezeEntitlement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: EntitlementFreezeRequest }) =>
      freezeEntitlement(id, body),
    onSuccess: () => invalidateAllEntitlements(qc),
  });
}

export function useUnfreezeEntitlement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unfreezeEntitlement(id),
    onSuccess: () => invalidateAllEntitlements(qc),
  });
}

export function useCancelEntitlement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: EntitlementCancelRequest }) =>
      cancelEntitlement(id, body),
    onSuccess: () => invalidateAllEntitlements(qc),
  });
}

// ---------------------------------------------------------------------------
// NOTE: The server now provides `days_until_expiry` and `percent_consumed`
// directly on EntitlementResponse (M1). The client-side compute helpers
// previously exported from this file were removed to avoid drift.
// ---------------------------------------------------------------------------


